import { logger } from '@/core/logger';
import { fetchFilters } from './contentfulClient';

type Locale = 'en' | 'ar';

const AMENITIES_FILTER_TYPE = 'Amenities';

/** One `filters` row of type "Amenities", keyed in the catalog by `value`. */
export interface AmenityCatalogEntry {
  readonly value: string;
  readonly label: string;
  readonly iconUrl: string | null;
  readonly order: number;
}

type AmenityCatalog = ReadonlyMap<string, AmenityCatalogEntry>;

const EMPTY_CATALOG: AmenityCatalog = new Map();

const catalogByLocale = new Map<Locale, AmenityCatalog>();
const inFlightByLocale = new Map<Locale, Promise<void>>();

/** Bumps when a locale catalog lands so React hooks can re-resolve labels. */
let catalogRevision = 0;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  catalogRevision += 1;
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore subscriber errors so one bad listener cannot block others
    }
  });
}

export function getAmenityCatalogRevision(): number {
  return catalogRevision;
}

export function subscribeAmenityCatalog(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Localized amenity catalog for `locale`. Empty until the central preload
 * resolves — callers fall back to the raw API amenity value meanwhile.
 */
export function getAmenityCatalog(locale: Locale): AmenityCatalog {
  return catalogByLocale.get(locale) ?? EMPTY_CATALOG;
}

function buildCatalog(
  rows: Awaited<ReturnType<typeof fetchFilters>>,
): AmenityCatalog {
  const catalog = new Map<string, AmenityCatalogEntry>();

  for (const row of rows) {
    if (
      row.filterType !== AMENITIES_FILTER_TYPE ||
      typeof row.value !== 'string' ||
      row.value.length === 0 ||
      catalog.has(row.value)
    ) {
      continue;
    }

    const label =
      typeof row.label === 'string' && row.label.trim().length > 0
        ? row.label
        : row.value;

    catalog.set(row.value, {
      value: row.value,
      label,
      iconUrl: row.iconUrl,
      order: typeof row.order === 'number' ? row.order : Number.MAX_SAFE_INTEGER,
    });
  }

  return catalog;
}

async function loadCatalog(locale: Locale): Promise<void> {
  try {
    const rows = await fetchFilters(locale, AMENITIES_FILTER_TYPE);
    catalogByLocale.set(locale, buildCatalog(rows));
    notifyListeners();
    logger.info('amenityCatalogService', 'Loaded amenity catalog', {
      locale,
      entries: catalogByLocale.get(locale)?.size ?? 0,
    });
  } catch (error) {
    logger.warn('amenityCatalogService', 'preloadAmenityCatalog failed', {
      locale,
      error: error instanceof Error ? error.message : String(error),
    });
  } finally {
    inFlightByLocale.delete(locale);
  }
}

/**
 * Loads the amenity catalog for `locale` once per language, alongside the
 * translation preload — shared across web and native (BloomCmsIcon renders
 * the resolved iconUrl via expo-image on both).
 */
/** Re-fetch the catalog even if one is cached; the current one stays until it arrives. */
export function refreshAmenityCatalog(locale: Locale): Promise<void> {
  const inFlight = inFlightByLocale.get(locale);
  if (inFlight) {
    return inFlight;
  }
  const request = loadCatalog(locale);
  inFlightByLocale.set(locale, request);
  return request;
}

export function preloadAmenityCatalog(locale: Locale): Promise<void> {
  if (catalogByLocale.has(locale)) {
    return Promise.resolve();
  }

  const inFlight = inFlightByLocale.get(locale);
  if (inFlight) {
    return inFlight;
  }

  const request = loadCatalog(locale);
  inFlightByLocale.set(locale, request);
  return request;
}
